
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Mail } from 'lucide-react';

interface SendReferralDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: (templateId: string, templateName: string) => Promise<void>;
  isSubmitting: boolean;
  templates: Array<{
    id: string;
    name: string;
    isDefault: boolean;
  }>;
}

const SendReferralDialog: React.FC<SendReferralDialogProps> = ({
  isOpen,
  onClose,
  onSend,
  isSubmitting,
  templates,
}) => {
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');

  const handleSend = async () => {
    if (!selectedTemplateId) return;
    
    const templateName = templates.find(t => t.id === selectedTemplateId)?.name || '';
    await onSend(selectedTemplateId, templateName);
    
    // Reset selection
    setSelectedTemplateId('');
    onClose();
  };
  
  // Find default template
  const defaultTemplate = templates.find(t => t.isDefault);
  
  // Set default template if not already selected
  React.useEffect(() => {
    if (isOpen && !selectedTemplateId && defaultTemplate) {
      setSelectedTemplateId(defaultTemplate.id);
    }
  }, [isOpen, selectedTemplateId, defaultTemplate]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Send Referral Invitation</DialogTitle>
          <DialogDescription>
            Choose a template to send a referral invitation to this user.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Template</label>
            <Select
              value={selectedTemplateId}
              onValueChange={setSelectedTemplateId}
              disabled={isSubmitting}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a template" />
              </SelectTrigger>
              <SelectContent>
                {templates.map(template => (
                  <SelectItem key={template.id} value={template.id}>
                    {template.name} {template.isDefault && '(Default)'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSend} 
            disabled={isSubmitting || !selectedTemplateId}
            className="flex items-center gap-2"
          >
            <Mail className="h-4 w-4" />
            {isSubmitting ? 'Sending...' : 'Send Invitation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SendReferralDialog;
